import React, { useState, useEffect } from 'react';
import Link from 'next/link';
import Head from 'next/head';
import InlineEmailOtpVerification from '@/components/common/InlineEmailOtpVerification';

const slides = [
    {
        badge: 'Elevate Your Career',
        title: (
            <span>
                Empowering <span className="text-brand-primary">Next-Gen</span> Leaders
            </span>
        ),
        description: 'Transform your leadership journey with L.I.S.T Learning Pathways. Master the art of influential leadership in the luxury retail sector through immersive, expert-led programs.',
        primaryBtn: 'Enroll Now',
        primaryLink: '/enrollment',
        secondaryBtn: 'Explore Programs',
        secondaryLink: '/programs',
        image: '/slider/1.jpg',
        bg: 'bg-white',
        dark: false
    },
    {
        badge: 'L.I.S.T Framework',
        title: (
            <span>Learning • Inspiring • Strategizing • Transforming</span>
        ),
        description: 'Our comprehensive leadership development approach builds on four foundational pillars designed to elevate your leadership capabilities from fundamentals to mastery.',
        primaryBtn: 'Begin Your Journey',
        primaryLink: '/enrollment',
        secondaryBtn: 'Meet Our Team',
        secondaryLink: '/about#team',
        image: '/slider/2.jpg',
        bg: 'bg-brand-dark',
        dark: true
    },
    {
        badge: 'Tailored Programs',
        title: (
            <span>
                Find the Strategic <span className="text-brand-primary">Leadership Path</span>
            </span>
        ),
        description: 'Choose from our 3-Day Immersive on-site programs or 4-Saturday flexible online courses designed to elevate your leadership capabilities.',
        primaryBtn: 'View All Programs',
        primaryLink: '/programs',
        secondaryBtn: 'Contact Us',
        secondaryLink: '/contact',
        image: '/slider/3.jpg',
        bg: 'bg-brand-primary/5',
        dark: false
    }
];
function TestimonialCard({ t }) {
    return (
        <div className="bg-white p-6 rounded-lg border border-gray-100 shadow-sm hover:shadow-xl hover:shadow-brand-primary/10 transition-all duration-300 group text-left">
            <div className="flex gap-1 mb-4">
                {[1, 2, 3, 4, 5].map((s) => (
                    <svg key={s} className="w-4 h-4 text-yellow-400 fill-current" viewBox="0 0 20 20">
                        <path d="M9.049 2.927c.3-.921 1.603-.921 1.902 0l1.07 3.292a1 1 0 00.95.69h3.462c.969 0 1.371 1.24.588 1.81l-2.8 2.034a1 1 0 00-.364 1.118l1.07 3.292c.3.921-.755 1.688-1.54 1.118l-2.8-2.034a1 1 0 00-1.175 0l-2.8 2.034c-.784.57-1.838-.197-1.539-1.118l1.07-3.292a1 1 0 00-.364-1.118L2.98 8.72c-.783-.57-.38-1.81.588-1.81h3.461a1 1 0 00.951-.69l1.07-3.292z"></path>
                    </svg>
                ))}
            </div>
            <p className="text-base text-gray-700 mb-4 leading-relaxed italic">"{t.text}"</p>
            <div>
                <h5 className="font-bold text-brand-dark">{t.author}</h5>
                <p className="text-gray-500 text-xs mt-0.5">{t.role}</p>
            </div>
        </div>
    );
}

const impactStories = [
    {
        text: "Rahil is the finest training professional we've worked with in over a decade. He owns any room, connects instinctively from senior management to frontline staff, and delivers sharp, specific solutions. He doesn't just train — he aligns. Every module is built with intent, and every session is memorable. A trusted partner who raises the bar and over-delivers, every time.",
        author: 'Platinum Guild International',
        role: 'Industry Partner'
    },
    {
        text: "We've seen a measurable shift in how our managers approach problem-solving and team inspiration after undergoing the L.I.S.T modules.",
        author: 'Dubai Duty Free',
        role: 'Retail Excellence Partner'
    }
];

const getImpactCardsPerView = (width) => {
    if (width >= 1024) return 3;
    if (width >= 768) return 2;
    return 1;
};

export default function Home() {
    const [currentSlide, setCurrentSlide] = useState(0);
    const [isSliderPaused, setIsSliderPaused] = useState(false);
    const [subscribeStatus, setSubscribeStatus] = useState('idle');
    const [subscribeMessage, setSubscribeMessage] = useState('');
    const [newsletterEmail, setNewsletterEmail] = useState('');
    const [verifiedNewsletterEmail, setVerifiedNewsletterEmail] = useState('');
    const [impactIndex, setImpactIndex] = useState(0);
    const [impactCardsPerView, setImpactCardsPerView] = useState(3);

    // Swipe state
    const [touchStart, setTouchStart] = useState(null);
    const [touchEnd, setTouchEnd] = useState(null);

    const minSwipeDistance = 50;

    const onTouchStart = (e) => {
        setTouchEnd(null);
        setTouchStart(e.targetTouches[0].clientX);
    };

    const onTouchMove = (e) => {
        setTouchEnd(e.targetTouches[0].clientX);
    };

    const onTouchEnd = () => {
        if (!touchStart || !touchEnd) return;
        const distance = touchStart - touchEnd;
        const isLeftSwipe = distance > minSwipeDistance;
        const isRightSwipe = distance < -minSwipeDistance;

        if (isLeftSwipe) {
            nextSlide();
        }
        if (isRightSwipe) {
            prevSlide();
        }
    };

    useEffect(() => {
        if (isSliderPaused) {
            return undefined;
        }

        const timer = setInterval(() => {
            setCurrentSlide((prev) => (prev + 1) % slides.length);
        }, 8000);

        return () => clearInterval(timer);
    }, [isSliderPaused]);

    useEffect(() => {
        const updateImpactLayout = () => {
            const cardsPerView = getImpactCardsPerView(window.innerWidth);
            setImpactCardsPerView(cardsPerView);
            setImpactIndex((prev) => Math.min(prev, Math.max(impactStories.length - cardsPerView, 0)));
        };

        updateImpactLayout();
        window.addEventListener('resize', updateImpactLayout);

        return () => window.removeEventListener('resize', updateImpactLayout);
    }, []);

    const nextSlide = () => setCurrentSlide((prev) => (prev + 1) % slides.length);
    const prevSlide = () => setCurrentSlide((prev) => (prev - 1 + slides.length) % slides.length);
    const maxImpactIndex = Math.max(impactStories.length - impactCardsPerView, 0);
    const nextImpactSlide = () => setImpactIndex((prev) => Math.min(prev + 1, maxImpactIndex));
    const prevImpactSlide = () => setImpactIndex((prev) => Math.max(prev - 1, 0));
    const activeSlide = slides[currentSlide];

    return (
        <>
            <Head>
                <title>L.I.S.T – Empowering Next-Gen Leaders</title>
                <meta name="description" content="L.I.S.T Learning Pathways focus on Learning, Inspiring, Strategizing, and Transforming." />
            </Head>

            <main className="min-h-screen">
                {/* Slider Hero Section - Redesigned with consistent height and better layout */}
                <section
                    className="relative h-[680px] sm:h-[700px] overflow-hidden"
                    onMouseEnter={() => setIsSliderPaused(true)}
                    onMouseLeave={() => setIsSliderPaused(false)}
                    onTouchStart={onTouchStart}
                    onTouchMove={onTouchMove}
                    onTouchEnd={onTouchEnd}
                >
                    {/* Background Image Layer */}
                    <div className="absolute inset-0">
                        <img
                            key={currentSlide}
                            src={activeSlide.image}
                            alt={activeSlide.badge}
                            className="w-full h-full object-cover object-center-top animate-in fade-in duration-700"
                            style={{ objectPosition: 'center 25%' }}
                        />
                        {/* Gradient Overlay */}
                        <div className={`absolute inset-0 transition-colors duration-700 ${activeSlide.dark
                            ? 'bg-gradient-to-r from-brand-dark via-brand-dark/90 to-brand-dark/40'
                            : 'bg-gradient-to-r from-white via-white/95 to-white/60'
                            }`}></div>
                        {/* Bottom Fade */}
                        <div className="absolute bottom-0 left-0 right-0 h-32 bg-gradient-to-t from-white to-transparent"></div>
                    </div>

                    {/* Content */}
                    <div className="relative z-10 h-full max-w-7xl mx-auto px-4 sm:px-6 lg:px-12">
                        <div className="h-full flex flex-col justify-center py-12 sm:py-16 pt-20 sm:pt-16">
                            <div className="max-w-3xl">
                                {/* Badge */}
                                <span className="inline-block px-4 py-1.5 bg-brand-primary text-white text-sm font-semibold rounded-full mb-5 shadow-lg shadow-brand-primary/25 animate-in slide-in-from-bottom-2 duration-500">
                                    {activeSlide.badge}
                                </span>

                                {/* Title */}
                                <h1 className={`text-2xl sm:text-4xl lg:text-5xl font-extrabold tracking-tight leading-[1.15] mb-6 ${activeSlide.dark ? 'text-white' : 'text-brand-dark'
                                    } animate-in slide-in-from-bottom-3 duration-500 delay-100`}>
                                    {activeSlide.title}
                                </h1>

                                {/* Description */}
                                <p className={`text-lg leading-relaxed mb-8 max-w-2xl ${activeSlide.dark ? 'text-gray-300' : 'text-gray-600'
                                    } animate-in slide-in-from-bottom-4 duration-500 delay-150`}>
                                    {activeSlide.description}
                                </p>

                                {/* Buttons */}
                                <div className="flex flex-col sm:flex-row gap-4 animate-in slide-in-from-bottom-5 duration-500 delay-200">
                                    <Link
                                        href={activeSlide.primaryLink}
                                        className="inline-flex items-center justify-center px-8 py-4 bg-brand-primary text-white font-semibold rounded-lg hover:shadow-xl hover:shadow-brand-primary/30 transition-all duration-300 hover:-translate-y-1"
                                    >
                                        {activeSlide.primaryBtn}
                                        <svg className="w-5 h-5 ml-2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M13 7l5 5m0 0l-5 5m5-5H6"></path>
                                        </svg>
                                    </Link>
                                    <Link
                                        href={activeSlide.secondaryLink}
                                        className={`inline-flex items-center justify-center px-8 py-4 border-2 font-semibold rounded-lg transition-all duration-300 hover:-translate-y-1 ${activeSlide.dark
                                            ? 'border-white/30 text-white hover:bg-white/10'
                                            : 'border-brand-dark/20 text-brand-dark hover:bg-brand-dark/5'
                                            }`}
                                    >
                                        {activeSlide.secondaryBtn}
                                    </Link>
                                </div>
                            </div>
                        </div>
                    </div>

                    {/* Slider Pagination */}
                    <div className="absolute bottom-6 left-1/2 -translate-x-1/2 z-20 flex items-center gap-2.5">
                        {slides.map((_, index) => (
                            <button
                                key={index}
                                onClick={() => setCurrentSlide(index)}
                                className={`h-2 rounded-full transition-all duration-300 ${index === currentSlide
                                    ? 'w-10 bg-brand-primary shadow-lg shadow-brand-primary/30'
                                    : 'w-2 bg-white/40 hover:bg-white/60'
                                    }`}
                                aria-label={`Slide ${index + 1}`}
                            ></button>
                        ))}
                    </div>

                    {/* Desktop Navigation Arrows */}
                    <button
                        onClick={prevSlide}
                        className="absolute left-6 top-1/2 -translate-y-1/2 z-20 hidden sm:flex w-14 h-14 rounded-full bg-brand-dark/90 hover:bg-brand-primary items-center justify-center text-white transition-all duration-300 hover:scale-110 shadow-lg"
                        aria-label="Previous slide"
                    >
                        <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2.5" d="M15 19l-7-7 7-7"></path>
                        </svg>
                    </button>
                    <button
                        onClick={nextSlide}
                        className="absolute right-6 top-1/2 -translate-y-1/2 z-20 hidden sm:flex w-14 h-14 rounded-full bg-brand-dark/90 hover:bg-brand-primary items-center justify-center text-white transition-all duration-300 hover:scale-110 shadow-lg"
                        aria-label="Next slide"
                    >
                        <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2.5" d="M9 5l7 7-7 7"></path>
                        </svg>
                    </button>
                </section>

                {/* What is L.I.S.T Section */}
                <section className="py-16 lg:py-20 bg-white relative overflow-hidden">
                    <div className="absolute top-0 right-0 w-64 h-64 bg-brand-primary/5 rounded-full blur-3xl translate-x-1/3 -translate-y-1/3"></div>
                    <div className="absolute bottom-0 left-0 w-80 h-80 bg-brand-primary/5 rounded-full blur-3xl -translate-x-1/3 translate-y-1/3"></div>

                    <div className="max-w-7xl mx-auto px-6 lg:px-12 relative z-10">
                        <div className="text-center max-w-2xl mx-auto mb-12">
                            <span className="inline-block px-4 py-1.5 bg-brand-primary text-white text-base font-bold rounded-full mb-3">
                                The Foundation
                            </span>
                            <h2 className="text-3xl lg:text-4xl font-extrabold text-brand-dark mb-4">
                                What is <span className="text-brand-primary">L.I.S.T?</span>
                            </h2>
                            <p className="text-gray-600 leading-relaxed text-base">
                                Our framework is built on four transformative pillars designed to elevate your leadership from fundamental skills to high-impact transformation.
                            </p>
                        </div>

                        <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                            {[
                                {
                                    l: 'L',
                                    title: 'LEARNING',
                                    desc: 'Building Your Leadership Foundation through self-awareness and emotional intelligence.',
                                    bgColor: 'bg-brand-primary/5',
                                    iconPath: 'M12 6.253v13m0-13C10.832 5.477 9.246 5 7.5 5S4.168 5.477 3 6.253v13C4.168 18.477 5.754 18 7.5 18s3.332.477 4.5 1.253m0-13C13.168 5.477 14.754 5 16.5 5c1.747 0 3.332.477 4.5 1.253v13C19.832 18.477 18.247 18 16.5 18c-1.746 0-3.332.477-4.5 1.253'
                                },
                                {
                                    l: 'I',
                                    title: 'INSPIRING',
                                    desc: 'Leading People with Purpose, building trust and psychological safety.',
                                    bgColor: 'bg-purple-50',
                                    iconPath: 'M4.318 6.318a4.5 4.5 0 000 6.364L12 20.364l7.682-7.682a4.5 4.5 0 00-6.364-6.364L12 7.636l-1.318-1.318a4.5 4.5 0 00-6.364 0l-2.8 2.034c-.784.57-1.838-.197-1.539-1.118l1.07-3.292a1 1 0 00-.364-1.118L2.98 8.72c-.783-.57-.38-1.81.588-1.81h3.461a1 1 0 00.951-.69l1.07-3.292z'
                                },
                                {
                                    l: 'S',
                                    title: 'STRATEGIZING',
                                    desc: 'Thinking Like a Leader with vision, energy management and decision-making.',
                                    bgColor: 'bg-teal-50',
                                    iconPath: 'M9.663 17h4.673M12 3v1m6.364 1.636l-.707.707M21 12h-1M4 12H3m3.343-5.657l-.707-.707m2.828 9.9a5 5 0 117.072 0l-.548.547A3.374 3.374 0 0014 18.469V19a2 2 0 11-4 0v-.531c0-.895-.356-1.754-.988-2.386l-.548-.547z'
                                },
                                {
                                    l: 'T',
                                    title: 'TRANSFORMING',
                                    desc: 'Driving Results & Lasting Impact through change leadership and team high-performance.',
                                    bgColor: 'bg-amber-50',
                                    iconPath: 'M13 10V3L4 14h7v7l9-11h-7z'
                                }
                            ].map((item, idx) => (
                                <div key={idx} className="bg-white p-6 rounded-md shadow-sm border border-gray-100 hover:shadow-xl hover:shadow-brand-dark/10 transition-all duration-500 group hover:-translate-y-1 relative overflow-hidden">
                                    <div className={`absolute top-0 left-0 right-0 h-1 bg-brand-primary opacity-0 group-hover:opacity-100 transition-opacity duration-300`}></div>
                                    <div className="flex gap-4">
                                        <div className={`w-14 h-14 ${item.bgColor} rounded-md flex items-center justify-center shrink-0 group-hover:scale-110 group-hover:rotate-3 transition-all duration-300`}>
                                            <svg className="w-7 h-7 text-brand-primary" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d={item.iconPath}></path>
                                            </svg>
                                        </div>
                                        <div className="flex-1">
                                            <span className={`inline-block px-2.5 py-0.5 bg-white border border-gray-100 text-base font-bold rounded-md mb-2 text-brand-primary`}>
                                                {item.l}
                                            </span>
                                            <h4 className="text-base font-bold mb-2 text-brand-dark">{item.title}</h4>
                                            <p className="text-gray-500 leading-relaxed text-base">{item.desc}</p>
                                        </div>
                                    </div>
                                </div>
                            ))}
                        </div>
                    </div>
                </section>

                {/* Program Options Section */}
                <section className="py-16 lg:py-20 bg-white relative overflow-hidden">
                    <div className="absolute top-0 right-0 w-80 h-80 bg-brand-primary/5 rounded-full blur-3xl translate-x-1/2 -translate-y-1/2"></div>
                    <div className="max-w-7xl mx-auto px-6 lg:px-12 relative z-10">
                        <div className="text-center max-w-2xl mx-auto mb-12 flex flex-col items-center">
                            <span className="text-brand-primary font-bold text-base mb-3 block">Choose Your Path</span>
                            <h2 className="text-3xl lg:text-4xl font-extrabold text-brand-dark mb-6">
                                Program <span className="text-brand-primary">Options</span>
                            </h2>
                            <Link href="/programs" className="text-brand-primary font-bold border-b-2 border-brand-primary/30 pb-1 hover:border-brand-primary hover:text-brand-dark transition-all tracking-wide text-base group">
                                View All Programs
                                <svg className="inline w-4 h-4 ml-2 group-hover:translate-x-2 transition-transform" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M17 8l4 4m0 0l-4 4m4-4H3"></path>
                                </svg>
                            </Link>
                        </div>

                        <div className="grid grid-cols-1 lg:grid-cols-3 gap-10">
                            {[
                                {
                                    id: 'ON-SITE',
                                    title: '3-Day Immersive',
                                    popular: false,
                                    desc: 'Intensive 18-hour hands-on experience in Mumbai. Ideal for deep transformation and peer networking.',
                                    features: ['18 Hours of Training', 'On-site Interaction', 'Capstone Journey', 'In-person Coaching'],
                                    link: '/programs/3-day-immersive'
                                },
                                {
                                    id: 'ONLINE',
                                    title: '4-Saturday Flexible',
                                    popular: true,
                                    desc: '12-hour comprehensive online program for busy professionals. Learn at your own pace over 4 weeks.',
                                    features: ['12 Hours of Training', 'Live Online Sessions', 'Toolkits & Resources', 'Digital Certificate'],
                                    link: '/programs/4-saturday-flexible'
                                },
                                {
                                    id: 'ONLINE',
                                    title: 'ODC 3-Hour Workshops',
                                    popular: false,
                                    desc: 'High-impact, shorter-format workshops for teams and professionals who need focused soft skills development without a full multi-week commitment.',
                                    features: ['Short Format Delivery', 'Practical Labs', 'Team-Centric Learning', 'Custom Workshop Paths'],
                                    link: '/programs/odc-workshops'
                                }
                            ].map((program, idx) => (
                                <div key={idx} className="flex flex-col bg-white border border-gray-100 rounded-md overflow-hidden shadow-sm hover:shadow-xl hover:shadow-brand-dark/5 transition-all duration-500 h-full group hover:-translate-y-2 relative">
                                    {program.popular && (
                                        <div className="absolute top-5 right-5 bg-brand-primary text-white text-base font-bold px-2.5 py-0.5 rounded-full z-10">
                                            Most Popular
                                        </div>
                                    )}
                                    <div className="bg-gray-50/50 p-6 border-b border-gray-100 relative overflow-hidden">
                                        <div className="absolute top-0 left-0 w-full h-1 bg-brand-primary"></div>
                                        <span className="inline-block px-3 py-1 bg-white border border-gray-100 text-base font-bold rounded-full mb-4 text-brand-dark shadow-sm">
                                            {program.id}
                                        </span>
                                        <h4 className="text-lg font-extrabold mb-3 text-brand-dark">{program.title}</h4>
                                        <p className="text-gray-500 text-base leading-relaxed">
                                            {program.desc}
                                        </p>
                                    </div>
                                    <div className="p-6 flex flex-col flex-grow">
                                        <ul className="space-y-3 mb-6">
                                            {program.features.map((feature, fIdx) => (
                                                <li key={fIdx} className="flex items-center text-base text-gray-600 font-medium">
                                                    <div className="w-5 h-5 rounded-full bg-brand-primary/10 flex items-center justify-center mr-4 group-hover:bg-brand-primary/20 transition-colors">
                                                        <svg className="w-3 h-3 text-brand-primary" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="3" d="M5 13l4 4L19 7"></path>
                                                        </svg>
                                                    </div>
                                                    {feature}
                                                </li>
                                            ))}
                                        </ul>
                                        <Link
                                            href={program.link}
                                            className="block w-full text-center py-3.5 bg-brand-primary text-white font-bold rounded-lg hover:shadow-xl hover:shadow-brand-primary/25 text-base mt-auto"
                                        >
                                            Explore Program
                                        </Link>
                                    </div>
                                </div>
                            ))}
                        </div>
                    </div>
                </section>

                {/* Who It's For Section */}
                <section className="py-16 lg:py-20 bg-brand-dark text-white overflow-hidden relative">
                    <div className="absolute top-0 right-0 w-1/2 h-full bg-brand-primary opacity-5 -skew-x-12 translate-x-1/2"></div>
                    <div className="absolute bottom-0 left-0 w-48 h-48 bg-brand-primary/10 rounded-full blur-3xl -translate-x-1/2 translate-y-1/2"></div>

                    <div className="max-w-7xl mx-auto px-6 lg:px-12 relative z-10">
                        <div className="lg:grid lg:grid-cols-2 lg:gap-16 items-center">
                            <div>
                                <span className="text-white font-bold text-base mb-3 block">Target Audience</span>
                                <h2 className="text-3xl lg:text-4xl font-extrabold mb-6 leading-tight">
                                    Who It's <span className="text-brand-primary">For</span>
                                </h2>
                                <p className="text-base text-gray-300 mb-8 leading-relaxed">
                                    Our programs are specifically curated for ambitious professionals in the luxury, retail, and hospitality sectors who are ready to transition from great contributors to exceptional leaders.
                                </p>
                                <div className="space-y-6">
                                    {[
                                        {
                                            title: 'Aspiring Managers',
                                            desc: 'Professionals looking to step into their first leadership role with confidence.',
                                            icon: (
                                                <svg className="w-8 h-8" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M13 7h8m0 0v8m0-8l-8 8-4-4-6 6"></path>
                                                </svg>
                                            )
                                        },
                                        {
                                            title: 'Current Leaders',
                                            desc: 'Seasoned managers wanting to refine their emotional intelligence and strategic vision.',
                                            icon: (
                                                <svg className="w-8 h-8" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M11.049 2.927c.3-.921 1.603-.921 1.902 0l1.07 3.292a1 1 0 00.95.69h3.462c.969 0 1.371 1.24.588 1.81l-2.8 2.034a1 1 0 00-.364 1.118l1.07 3.292c.3.921-.755 1.688-1.54 1.118l-2.8-2.034a1 1 0 00-1.175 0l-2.8 2.034c-.784.57-1.838-.197-1.539-1.118l1.07-3.292a1 1 0 00-.364-1.118L2.98 8.72c-.783-.57-.38-1.81.588-1.81h3.461a1 1 0 00.951-.69l1.07-3.292z"></path>
                                                </svg>
                                            )
                                        }
                                    ].map((item, idx) => (
                                        <div key={idx} className="flex gap-5 border-l-2 border-brand-primary/30 pl-6 py-2.5 hover:border-brand-primary hover:pl-8 transition-all duration-300 group cursor-pointer">
                                            <div className="w-10 h-10 rounded-lg bg-brand-primary/20 flex items-center justify-center group-hover:bg-brand-primary group-hover:scale-110 transition-all duration-300 text-brand-primary group-hover:text-white shrink-0">
                                                {item.icon}
                                            </div>
                                            <div>
                                                <h4 className="text-base font-bold mb-1.5 text-white group-hover:text-brand-primary transition-colors">
                                                    {item.title}
                                                </h4>
                                                <p className="text-gray-300 text-base leading-relaxed">{item.desc}</p>
                                            </div>
                                        </div>
                                    ))}
                                </div>
                            </div>

                            <div className="mt-12 lg:mt-0 relative">
                                <div className="aspect-square relative rounded-lg overflow-hidden shadow-xl">
                                    <img
                                        src="/list-hero/3.jpg"
                                        alt="Leadership Workshop"
                                        className="w-full h-full object-cover grayscale opacity-40 hover:grayscale-0 hover:opacity-100 transition-all duration-1000"
                                    />
                                    <div className="absolute inset-0 bg-brand-dark/30"></div>
                                </div>
                                <div className="absolute -bottom-6 -left-6 bg-white p-6 rounded-lg shadow-lg hidden md:block border border-gray-100 max-w-xs animate-bounce-subtle">
                                    <p className="text-brand-dark font-bold text-lg mb-2">"Inspiring leaders to cultivate growth, innovation, and team excellence."</p>
                                    <span className="text-brand-primary font-bold text-base">L.I.S.T Philosophy</span>
                                </div>
                            </div>
                        </div>
                    </div>
                </section>

                {/* Testimonials Section */}
                <section className="py-16 lg:py-20 bg-white overflow-hidden relative">
                    <div className="max-w-7xl mx-auto px-6 lg:px-12">
                        <div className="flex items-start justify-between mb-12 gap-4">
                            <div>
                                <span className="text-brand-primary font-bold text-base mb-2 block">Validation</span>
                                <h2 className="text-3xl lg:text-4xl font-extrabold text-brand-dark">
                                    Impact <span className="text-brand-primary">Stories</span>
                                </h2>
                            </div>
                            <div className="flex items-center gap-3">
                                <button
                                    type="button"
                                    onClick={prevImpactSlide}
                                    disabled={impactIndex === 0}
                                    className="w-11 h-11 rounded-full border border-gray-200 bg-white text-brand-dark flex items-center justify-center shadow-sm transition-all hover:border-brand-primary hover:text-brand-primary disabled:opacity-40 disabled:cursor-not-allowed"
                                    aria-label="Previous testimonial"
                                >
                                    <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M15 19l-7-7 7-7"></path>
                                    </svg>
                                </button>
                                <button
                                    type="button"
                                    onClick={nextImpactSlide}
                                    disabled={impactIndex >= maxImpactIndex}
                                    className="w-11 h-11 rounded-full border border-gray-200 bg-white text-brand-dark flex items-center justify-center shadow-sm transition-all hover:border-brand-primary hover:text-brand-primary disabled:opacity-40 disabled:cursor-not-allowed"
                                    aria-label="Next testimonial"
                                >
                                    <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M9 5l7 7-7 7"></path>
                                    </svg>
                                </button>
                            </div>
                        </div>

                        <div className="overflow-hidden">
                            <div
                                className="flex transition-transform duration-500 ease-out"
                                style={{
                                    transform: `translateX(-${impactIndex * (100 / impactCardsPerView)}%)`,
                                }}
                            >
                                {impactStories.map((t, i) => (
                                    <div
                                        key={i}
                                        className="shrink-0 px-0 md:px-3"
                                        style={{ width: `${100 / impactCardsPerView}%` }}
                                    >
                                        <TestimonialCard t={t} />
                                    </div>
                                ))}
                            </div>
                        </div>
                    </div>
                </section>

                {/* Certifications Section */}
                <section className="py-16 lg:py-20 bg-gray-50/50 relative overflow-hidden">
                    <div className="max-w-7xl mx-auto px-6 lg:px-12 relative z-10">
                        <div className="text-center max-w-2xl mx-auto mb-12">
                            <span className="inline-block px-4 py-1.5 bg-brand-primary text-white text-base font-bold rounded-full mb-3">
                                Trust & Credibility
                            </span>
                            <h2 className="text-3xl lg:text-4xl font-extrabold text-brand-dark mb-3">
                                Certifications & <span className="text-brand-primary">Accreditations</span>
                            </h2>
                            <p className="text-gray-600 text-base">Our commitment to excellence is recognized through industry-leading certifications.</p>
                        </div>



                        <div className="mt-10 grid grid-cols-1 md:grid-cols-2 gap-6">
                            <div className="overflow-hidden rounded-lg shadow-lg border border-gray-100 bg-white p-4 flex items-center justify-center">
                                <img
                                    src="/CPD C 786683.png"
                                    alt="CPD Certification"
                                    className="w-full h-auto object-contain max-h-[300px]"
                                />
                            </div>
                            <div className="overflow-hidden rounded-lg shadow-lg border border-gray-100 bg-white p-4 flex items-center justify-center">
                                <img
                                    src="/ISTD affiliation.jpg"
                                    alt="ISTD Affiliation"
                                    className="w-full h-auto object-contain max-h-[300px]"
                                />
                            </div>
                        </div>
                    </div>
                </section>



                {/* Final CTA Section */}
                <section className="py-16 lg:py-20 bg-brand-dark relative overflow-hidden text-white">
                    <div className="max-w-7xl mx-auto px-6 lg:px-12 relative z-10 w-full">
                        <div className="text-center w-full max-w-4xl mx-auto relative overflow-hidden">
                            <div className="relative z-10 mx-auto">
                                <h2 className="text-4xl md:text-5xl font-extrabold text-white mb-8">L.I.S.T. Programs</h2>
                                <div className="flex flex-col sm:flex-row justify-center gap-4">
                                    <Link href="/enrollment" className="px-10 py-4 bg-brand-primary text-white font-bold rounded-md hover:shadow-xl hover:shadow-brand-primary/30 transition-all active:scale-95 transform hover:-translate-y-1 text-base">
                                        Enroll Now
                                    </Link>
                                    <Link href="/contact" className="px-10 py-4 bg-transparent border-2 border-white/20 text-white font-bold rounded-md hover:bg-white/10 hover:border-white transition-all active:scale-95 text-base text-center">
                                        Contact Us
                                    </Link>
                                </div>

                                <div className="flex flex-wrap justify-between gap-8 mt-12 pt-8 border-t border-white/10 max-w-3xl mx-auto">
                                    {[
                                        { v: '500+', l: 'Leaders Trained' },
                                        { v: '98%', l: 'Success Rate' }
                                    ].map((s, i) => (
                                        <div key={i} className="text-center">
                                            <p className="text-3xl md:text-5xl font-extrabold text-white mb-2">{s.v}</p>
                                            <p className="text-gray-300 text-sm md:text-base">{s.l}</p>
                                        </div>
                                    ))}
                                </div>
                            </div>
                        </div>
                    </div>
                </section>

                {/* Newsletter Section */}
                <section className="py-16 lg:py-20 bg-gray-50/50 relative overflow-hidden">
                    <div className="absolute top-0 left-1/2 -translate-x-1/2 w-full h-full bg-brand-primary/5"></div>
                    <div className="max-w-7xl mx-auto px-6 lg:px-12 relative z-10 text-center">
                        <div className="max-w-4xl mx-auto flex flex-col items-center">
                            <div className="w-12 h-12 bg-brand-primary rounded-md flex items-center justify-center mb-4 shadow-lg shadow-brand-primary/20">
                                <svg className="w-6 h-6 text-white" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M3 8l7.89 5.26a2 2 0 002.22 0L21 8M5 19h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v10a2 2 0 002 2z"></path>
                                </svg>
                            </div>
                            <h2 className="text-3xl lg:text-4xl font-extrabold mb-4 text-brand-dark">
                                Stay <span className="text-brand-primary">Influential</span>
                            </h2>
                            <p className="text-gray-500 mb-8 max-w-lg text-base">
                                Get leadership insights, program updates, and exclusive resources delivered to your inbox.
                            </p>

                            {subscribeStatus === 'success' ? (
                                <div className="bg-white border border-green-100 rounded-2xl p-10 shadow-xl shadow-green-500/5 animate-bounce-in max-w-md">
                                    <div className="w-16 h-16 bg-green-500 rounded-full flex items-center justify-center mx-auto mb-6">
                                        <svg className="w-8 h-8 text-white" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={3} d="M5 13l4 4L19 7" />
                                        </svg>
                                    </div>
                                    <h3 className="text-2xl font-black text-brand-dark mb-2">You're Subscribed!</h3>
                                    <p className="text-gray-500">Welcome to the inner circle. Expect greatness in your inbox soon.</p>
                                </div>
                            ) : (
                                <div className="w-full max-w-2xl">
                                    <form
                                        className="flex flex-col sm:flex-row gap-4 mb-4"
                                        onSubmit={async (e) => {
                                            e.preventDefault();
                                            if (!newsletterEmail) return;

                                            if (verifiedNewsletterEmail !== newsletterEmail.trim().toLowerCase()) {
                                                setSubscribeStatus('error');
                                                setSubscribeMessage('Verify your email with OTP before subscribing.');
                                                return;
                                            }

                                            setSubscribeStatus('loading');
                                            try {
                                                const res = await fetch('/api/newsletter/subscribe', {
                                                    method: 'POST',
                                                    headers: { 'Content-Type': 'application/json' },
                                                    body: JSON.stringify({ email: newsletterEmail })
                                                });
                                                const data = await res.json();
                                                if (data.success) {
                                                    setSubscribeStatus('success');
                                                    setSubscribeMessage(data.message);
                                                } else {
                                                    setSubscribeStatus('error');
                                                    setSubscribeMessage(data.message || 'Something went wrong');
                                                    setTimeout(() => setSubscribeStatus('idle'), 5000);
                                                }
                                            } catch (err) {
                                                setSubscribeStatus('error');
                                                setSubscribeMessage('Failed to subscribe. Please try again.');
                                                setTimeout(() => setSubscribeStatus('idle'), 5000);
                                            }
                                        }}
                                    >
                                        <input
                                            name="email"
                                            type="email"
                                            value={newsletterEmail}
                                            onChange={(e) => setNewsletterEmail(e.target.value)}
                                            placeholder="Enter your email address"
                                            className="flex-1 px-6 py-4 rounded-md bg-white border border-gray-100 text-brand-dark focus:outline-none focus:ring-2 focus:ring-brand-primary/20 transition-all shadow-sm hover:shadow-md text-base"
                                            required
                                            disabled={subscribeStatus === 'loading'}
                                        />
                                        <button
                                            type="submit"
                                            disabled={subscribeStatus === 'loading'}
                                            className="bg-brand-primary text-white px-10 py-4 rounded-md font-bold hover:shadow-xl hover:shadow-brand-primary/30 transition-all active:scale-95 transform hover:-translate-y-1 text-base whitespace-nowrap disabled:opacity-50"
                                        >
                                            {subscribeStatus === 'loading' ? 'Joining...' : 'Join Now'}
                                        </button>
                                    </form>
                                    <div className="mb-4">
                                        <InlineEmailOtpVerification
                                            email={newsletterEmail}
                                            verifiedEmail={verifiedNewsletterEmail}
                                            onVerifiedEmailChange={setVerifiedNewsletterEmail}
                                            context="newsletter"
                                            mode="manual"
                                        />
                                    </div>
                                    {subscribeStatus === 'error' && (
                                        <p className="text-red-500 font-bold text-sm animate-shake">{subscribeMessage}</p>
                                    )}
                                </div>
                            )}
                        </div>
                    </div>
                </section>
            </main>
        </>
    );
}
